import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { workQueueApi } from '@/api/workQueue.api';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { StatCard } from '@/components/ui/StatCard';
import { WorkQueueItemCard } from './WorkQueueItemCard';
import type { ErpModule } from '@/api/platform.api';
import type { WorkQueueListResponse } from '@/types/workQueue';
import { showApiError } from '@/utils/apiError';

interface ModuleWorkQueuePanelProps {
  module: ErpModule;
  title?: string;
  limit?: number;
}

export function ModuleWorkQueuePanel({ module, title = "Today's work", limit = 5 }: ModuleWorkQueuePanelProps) {
  const [data, setData] = useState<WorkQueueListResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await workQueueApi.list({ scope: 'MY', module });
      setData(res);
    } catch (err) {
      showApiError(err, 'Failed to load work queue');
    } finally {
      setLoading(false);
    }
  }, [module]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <p className="text-sm text-slate-500">Loading work queue…</p>;
  }

  const summary = data?.summary;
  const items = (data?.items ?? []).slice(0, limit);

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
        <Link to="/work" className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400">
          My Work →
        </Link>
      </div>
      {summary && (
        <KpiGrid columns={4}>
          <StatCard title="Due today" value={summary.dueToday} />
          <StatCard title="Overdue" value={summary.overdue} />
          <StatCard title="Approvals" value={summary.waitingApproval} />
          <StatCard title="Assigned to me" value={summary.assignedToMe} />
        </KpiGrid>
      )}
      {items.length === 0 ? (
        <div className="card p-4 text-sm text-slate-500">No pending work in this module — you're caught up.</div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <WorkQueueItemCard key={item.id} item={item} onActionComplete={load} />
          ))}
        </div>
      )}
    </section>
  );
}
